(() => {
  const parseJSON = (/** @type {string} */ jsonString) => {
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('Invalid JSON:', error.message);
      return null;
    }
  };

  const getGlobalValues = () => {
    const jsonNode = document.querySelector('#DataLayerGlobalValuesJSON');

    if (!jsonNode?.textContent) {
      console.error('Global values script tag is missing or empty');
      return null;
    }

    return parseJSON(jsonNode.textContent.trim());
  };

  /** @type {Object.<string, any>} */
  const globalValues = getGlobalValues();

  if (!globalValues) return;

  const REGION_MAP = parseJSON(globalValues.regionMap);
  const PAGE_TYPE_MAP = parseJSON(globalValues.pageTypeMap);
  const SITE_SECTION_MAP = parseJSON(globalValues.siteSectionMap);
  const ADD_TO_CART_TYPE_MAP = parseJSON(globalValues.addToCartTypeMap);

  /**
   * @typedef {Object} FormatterParams
   * @property {any} eventObj
   */
  window.dataLayerFormatters({
    /**
     * @param {FormatterParams} params
     */
    page_viewed: ({ eventObj }) => {
      const countryCode = globalValues.localization?.country?.isoCode;

      return {
        event: 'page_view',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || 'undefined',
        user_id: globalValues.user?.id || 'undefined',
        user_locale: globalValues.user?.id && countryCode ? countryCode : 'undefined',
        page_locale: countryCode || 'undefined',
        region:
          Object.keys(REGION_MAP).find((region) => REGION_MAP[region].split(',').includes(countryCode)) || 'undefined',
        site_type: 'Ecommerce',
        page_type:
          Object.keys(PAGE_TYPE_MAP).find((pageType) =>
            PAGE_TYPE_MAP[pageType].split(',').includes(globalValues.template?.name)
          ) || 'undefined',
        site_section:
          Object.keys(SITE_SECTION_MAP).find((siteSection) =>
            SITE_SECTION_MAP[siteSection]
              .split(',')
              .includes(globalValues.template?.suffix || globalValues.template?.name)
          ) || 'undefined',
        site_subsection: 'undefined',
        brand: globalValues.brandName || 'undefined',
      };
    },
    /**
     * @param {FormatterParams} params
     */
    product_viewed: ({ eventObj }) => {
      return {
        event: 'view_item',
        view_item_type: 'Ecommerce',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || 'undefined',
        ecommerce: {
          currency: eventObj?.data?.productVariant?.price?.currencyCode || 'undefined',
          value: eventObj?.data?.productVariant?.price?.amount || 'undefined',
          items: [
            {
              item_id: eventObj?.data?.productVariant?.id || 'undefined',
              item_name:
                `${eventObj?.data?.productVariant?.product?.title} - ${eventObj?.data?.productVariant?.title}`.replace(
                  ' - Default Title',
                  ''
                ) || 'undefined',
              quantity: 1,
              price: eventObj?.data?.productVariant?.price?.amount || 'undefined',
              affiliation: eventObj?.data?.productVariant?.product?.type || 'undefined',
              coupon: 'undefined',
              discount: globalValues.variant?.compareAtPrice
                ? globalValues.variant?.compareAtPrice - globalValues.variant?.price
                : 'undefined',
              index: 'undefined',
              item_brand: eventObj?.data?.productVariant?.product?.vendor || 'undefined',
              item_category: 'Products',
              item_category2: 'undefined',
              item_category3: 'undefined',
              item_category4: 'undefined',
              item_category5: 'undefined',
              item_list_id: 'undefined',
              item_list_name: 'undefined',
              item_variant:
                eventObj?.data?.productVariant?.title === 'Default Title'
                  ? 'undefined'
                  : eventObj?.data?.productVariant?.title,
              location_id: globalValues.variant.locationId || 'undefined',
            },
          ],
        },
      };
    },
    /**
     * @param {FormatterParams} params
     */
    product_added_to_cart: ({ eventObj }) => {
      return {
        event: 'add_to_cart',
        add_to_cart_type:
          Object.keys(ADD_TO_CART_TYPE_MAP).find((pageType) =>
            ADD_TO_CART_TYPE_MAP[pageType].split(',').includes(globalValues.template?.name)
          ) || 'undefined',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || 'undefined',
        ecommerce: {
          currency: eventObj?.data?.cartLine?.cost?.totalAmount?.currencyCode || 'undefined',
          value: eventObj?.data?.cartLine?.cost?.totalAmount?.amount || 'undefined',
          items: [
            {
              item_id: eventObj?.data?.cartLine?.merchandise?.id || 'undefined',
              item_name: eventObj?.data?.cartLine?.merchandise?.product?.title || 'undefined',
              quantity: eventObj?.data?.cartLine?.quantity || 'undefined',
              price: eventObj?.data?.cartLine?.merchandise?.price?.amount || 'undefined',
              affiliation: eventObj?.data?.cartLine?.merchandise?.product?.type || 'undefined',
              coupon: 'undefined',
              discount: 'undefined',
              index: 'undefined',
              item_brand: eventObj?.data?.cartLine?.merchandise?.product?.vendor || 'undefined',
              item_category: 'Products',
              item_category2: 'undefined',
              item_category3: 'undefined',
              item_category4: 'undefined',
              item_category5: 'undefined',
              item_list_id: 'undefined',
              item_list_name: 'undefined',
              item_variant: eventObj?.data?.cartLine?.merchandise?.title || 'undefined',
              location_id: 'undefined',
            },
          ],
        },
      };
    },
    /**
     * @param {FormatterParams} params
     */
    product_removed_from_cart: ({ eventObj }) => {
      return {
        event: 'remove_from_cart',
        remove_from_cart_type: 'Cart',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || 'undefined',
        ecommerce: {
          currency: eventObj?.data?.cartLine?.cost?.totalAmount?.currencyCode || 'undefined',
          value: eventObj?.data?.cartLine?.cost?.totalAmount?.amount || 'undefined',
          items: [
            {
              item_id: eventObj?.data?.cartLine?.merchandise?.id || 'undefined',
              item_name: eventObj?.data?.cartLine?.merchandise?.product?.title || 'undefined',
              quantity: eventObj?.data?.cartLine?.quantity || 'undefined',
              price: eventObj?.data?.cartLine?.merchandise?.price?.amount || 'undefined',
              affiliation: eventObj?.data?.cartLine?.merchandise?.product?.type || 'undefined',
              coupon: 'undefined',
              discount: 'undefined',
              index: 'undefined',
              item_brand: eventObj?.data?.cartLine?.merchandise?.product?.vendor || 'undefined',
              item_category: 'Products',
              item_category2: 'undefined',
              item_category3: 'undefined',
              item_category4: 'undefined',
              item_category5: 'undefined',
              item_list_id: 'undefined',
              item_list_name: 'undefined',
              item_variant: eventObj?.data?.cartLine?.merchandise?.title || 'undefined',
              location_id: 'undefined',
            },
          ],
        },
      };
    },
    /**
     * @param {FormatterParams} params
     */
    cart_viewed: ({ eventObj }) => {
      return {
        event: 'view_cart',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || 'undefined',
        ecommerce: {
          currency: eventObj?.data?.cart?.cost?.totalAmount?.currencyCode || 'undefined',
          value: eventObj?.data?.cart?.cost?.totalAmount?.amount || 'undefined',
          items:
            eventObj?.data?.cart?.lines?.map(
              /** @param {any} line,  @param {number} index */
              (line, index) => ({
                item_id: line?.merchandise?.id || 'undefined',
                item_name:
                  `${line?.merchandise?.product?.title} - ${line?.merchandise?.title}`.replace(
                    ' - Default Title',
                    ''
                  ) || 'undefined',
                quantity: line?.quantity || 'undefined',
                price: line?.merchandise?.price?.amount || 'undefined',
                affiliation: line?.merchandise?.product?.type || 'undefined',
                coupon: 'undefined',
                discount: 'undefined',
                index: index,
                item_brand: line?.merchandise?.product?.vendor || 'undefined',
                item_category: 'Products',
                item_category2: 'undefined',
                item_category3: 'undefined',
                item_category4: 'undefined',
                item_category5: 'undefined',
                item_list_id: 'undefined',
                item_list_name: 'undefined',
                item_variant: line?.merchandise?.title === 'Default Title' ? 'undefined' : line?.merchandise?.title,
                location_id: 'undefined',
              })
            ) || 'undefined',
        },
      };
    },
    /**
     * @param {FormatterParams} params
     */
    search_submitted: ({ eventObj }) => {
      setTimeout(() => {
        window.dataLayerPush({
          event: 'search',
          data: {
            timestamp: new Date().toISOString(),
            data: {
              query: eventObj?.data?.searchResult?.query,
            },
          },
        });
      });

      const searchResultsViewedEvent = {
        event: 'view_item_list',
        item_list_type: 'Search Results',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || 'undefined',
        ecommerce: {
          currency: eventObj?.data?.searchResult?.productVariants?.[0]?.price?.currencyCode || 'undefined',
          value:
            Number(
              [...(eventObj?.data?.searchResult?.productVariants || [])]
                .reduce((sum, v) => sum + (Number(v?.price?.amount) || 0), 0)
                .toFixed(2)
            ) || 'undefined',
          items:
            eventObj?.data?.searchResult?.productVariants?.map(
              /** @param {any} variant,  @param {number} index */
              (variant, index) => ({
                id: variant?.product?.id || 'undefined',
                item_id: variant?.id || 'undefined',
                item_name:
                  `${variant?.product?.title} - ${variant?.title}`.replace(' - Default Title', '') || 'undefined',
                quantity: 1,
                price: variant?.price?.amount || 'undefined',
                affiliation: variant?.product?.type || 'undefined',
                coupon: 'undefined',
                discount: 'undefined',
                index: index,
                item_brand: variant?.product?.vendor || 'undefined',
                item_category: 'Products',
                item_category2: 'undefined',
                item_category3: 'undefined',
                item_category4: 'undefined',
                item_category5: 'undefined',
                item_list_id: 'undefined',
                item_list_name: 'undefined',
                item_variant: variant?.title === 'Default Title' ? 'undefined' : variant?.title,
                location_id: 'undefined',
              })
            ) || 'undefined',
        },
      };

      localStorage.setItem('lastViewedCollectionData', JSON.stringify(searchResultsViewedEvent));

      return {
        ...searchResultsViewedEvent,
        ecommerce: {
          ...searchResultsViewedEvent.ecommerce,
          items:
            searchResultsViewedEvent.ecommerce.items.map(
              /** @param {any} item */
              (item) => {
                const { id, ...rest } = item;
                return rest;
              }
            ) || 'undefined',
        },
      };
    },
    /**
     * @param {FormatterParams} params
     */
    collection_viewed: ({ eventObj }) => {
      const collectionViewedEvent = {
        event: 'view_item_list',
        item_list_type: eventObj.data?.collection?.title || 'undefined',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || 'undefined',
        ecommerce: {
          currency: eventObj?.data?.collection?.productVariants?.[0]?.price?.currencyCode || 'undefined',
          value:
            Number(
              [...(eventObj?.data?.collection?.productVariants || [])]
                .reduce((sum, v) => sum + (Number(v?.price?.amount) || 0), 0)
                .toFixed(2)
            ) || 'undefined',
          items:
            eventObj?.data?.collection?.productVariants?.map(
              /** @param {any} variant,  @param {number} index */
              (variant, index) => ({
                id: variant?.product?.id || 'undefined',
                item_id: variant?.id || 'undefined',
                item_name:
                  `${variant?.product?.title} - ${variant?.title}`.replace(' - Default Title', '') || 'undefined',
                quantity: 1,
                price: variant?.price?.amount || 'undefined',
                affiliation: variant?.product?.type || 'undefined',
                coupon: 'undefined',
                discount: 'undefined',
                index: index,
                item_brand: variant?.product?.vendor || 'undefined',
                item_category: 'Products',
                item_category2: 'undefined',
                item_category3: 'undefined',
                item_category4: 'undefined',
                item_category5: 'undefined',
                item_list_id: globalValues.collection?.id || 'undefined',
                item_list_name: globalValues.collection?.title || 'undefined',
                item_variant: variant?.title === 'Default Title' ? 'undefined' : variant?.title,
                location_id: 'undefined',
              })
            ) || 'undefined',
        },
      };

      localStorage.setItem('lastViewedCollectionData', JSON.stringify(collectionViewedEvent));

      return {
        ...collectionViewedEvent,
        ecommerce: {
          ...collectionViewedEvent.ecommerce,
          items:
            collectionViewedEvent.ecommerce.items.map(
              /** @param {any} item */
              (item) => {
                const { id, ...rest } = item;
                return rest;
              }
            ) || 'undefined',
        },
      };
    },
    /**
     * @param {FormatterParams} params
     */
    search: ({ eventObj }) => {
      return {
        event: 'search',
        search_term: 'undefined',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || 'undefined',
      };
    },
    /**
     * @param {FormatterParams} params
     */
    cart_drawer_viewed: ({ eventObj }) => {
      return {
        event: 'view_cart',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || 'undefined',
        ecommerce: {
          currency: eventObj?.data?.cart?.currency || 'undefined',
          value: eventObj?.data?.cart?.total_price || 'undefined',
          items:
            eventObj?.data?.cart?.items?.map(
              /** @param {any} line,  @param {number} index */
              (line, index) => ({
                item_id: line?.variant_id || 'undefined',
                item_name: `${line?.product_title} - ${line?.variant_title}`.replace(' - null', '') || 'undefined',
                quantity: line?.quantity || 'undefined',
                price: line?.final_line_price || 'undefined',
                affiliation: line?.product_type || 'undefined',
                coupon: line.discounts?.[0]?.title || 'undefined',
                discount: line.total_discount || 'undefined',
                index: index,
                item_brand: line?.merchandise?.product?.vendor || 'undefined',
                item_category: 'Products',
                item_category2: 'undefined',
                item_category3: 'undefined',
                item_category4: 'undefined',
                item_category5: 'undefined',
                item_list_id: 'undefined',
                item_list_name: 'undefined',
                item_variant: line?.variant_title == null ? 'undefined' : line?.variant_title,
                location_id: 'undefined',
              })
            ) || 'undefined',
        },
      };
    },
    /**
     * @param {FormatterParams} params
     */
    view_item_list: ({ eventObj }) => {
      return {
        event: 'view_item_list',
        item_list_type: eventObj?.data?.sectionName || 'undefined',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || 'undefined',
        ecommerce: {
          currency: eventObj?.data?.payload?.currency || 'undefined',
          value: eventObj?.data?.payload?.value || 'undefined',
          items:
            eventObj?.data?.payload?.items?.map(
              /** @param {any} item */
              (item) => {
                const { id, ...rest } = item;
                return rest;
              }
            ) || 'undefined',
        },
      };
    },
    /**
     * @param {FormatterParams} params
     */
    collection_selected_item: ({ eventObj }) => {
      return {
        event: 'select_item',
        select_item_type: 'Ecommerce',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || 'undefined',
        ecommerce: {
          currency: eventObj?.data?.payload?.currency || 'undefined',
          value: eventObj?.data?.payload?.value || 'undefined',
          items: (({ id, ...rest }) => rest)(eventObj?.data?.payload?.item || {}) || 'undefined',
        },
      };
    },
    /**
     * @param {FormatterParams} params
     */
    select_item: ({ eventObj }) => {
      return {
        event: 'select_item',
        select_item_type: 'Ecommerce',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || 'undefined',
        ecommerce: {
          items: (({ id, ...rest }) => rest)(eventObj?.data?.payload?.item || {}) || 'undefined',
        },
      };
    },
    /**
     * @param {FormatterParams} params
     */
    video_start: ({ eventObj }) => {
      return {
        event: 'video_start',
        video_title: eventObj?.data?.videoTitle || 'undefined',
        video_url: eventObj?.data?.videoSrc || 'undefined',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || 'undefined',
      };
    },
    /**
     * @param {FormatterParams} params
     */
    video_progress: ({ eventObj }) => {
      return {
        event: 'video_progress',
        video_title: eventObj?.data?.videoTitle || 'undefined',
        video_url: eventObj?.data?.videoSrc || 'undefined',
        video_completion: eventObj?.data?.progress ? eventObj?.data?.progress + '%' : 'undefined',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || 'undefined',
      };
    },
    /**
     * @param {FormatterParams} params
     */
    video_complete: ({ eventObj }) => {
      return {
        event: 'video_complete',
        video_title: eventObj?.data?.videoTitle || 'undefined',
        video_url: eventObj?.data?.videoSrc || 'undefined',
        video_completion: eventObj?.data?.progress ? eventObj?.data?.progress + '%' : 'undefined',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || 'undefined',
      };
    },
    /**
     * @param {FormatterParams} params
     */
    form_start: ({ eventObj }) => {
      return {
        event: 'form_start',
        form_type: eventObj?.data?.formType || 'undefined',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || 'undefined',
      };
    },
    /**
     * @param {FormatterParams} params
     */
    form_submit: ({ eventObj }) => {
      return {
        event: 'form_submit',
        form_type: eventObj?.data?.formType || 'undefined',
        status: eventObj?.data?.status || 'undefined',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || 'undefined',
      };
    },
    /**
     * @param {FormatterParams} params
     */
    newsletter_signup: ({ eventObj }) => {
      return {
        event: 'newsletter_signup',
        form_type: eventObj?.data?.formType || 'undefined',
        status: eventObj?.data?.status || 'undefined',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || 'undefined',
        newsletter_optin_type: eventObj?.data?.newsletterOptinType || 'undefined',
      };
    },
    /**
     * @param {FormatterParams} params
     */
    content_interaction: ({ eventObj }) => {
      return {
        event: 'content_interaction',
        journey_type: eventObj?.data?.journeyType || 'undefined',
        interaction_type: globalValues.pageTitle || 'undefined',
        interaction_name: eventObj?.data?.interactionName || 'undefined',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || 'undefined',
      };
    },
    /**
     * @param {FormatterParams} params
     */
    outbound_click: ({ eventObj }) => {
      return {
        event: 'outbound_click',
        link_name: eventObj?.data?.linkName || 'undefined',
        link_url: eventObj?.data?.linkUrl || 'undefined',
        outbound_link_type: eventObj?.data?.outboundLinkType || 'undefined',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || 'undefined',
      };
    },
    /**
     * @param {FormatterParams} params
     */
    navigation_click: ({ eventObj }) => {
      return {
        event: 'navigation_click',
        journey_type: eventObj?.data?.journeyType || 'undefined',
        navigation_type: eventObj?.data?.navigationType || 'undefined',
        interaction_name: eventObj?.data?.interactionName || 'undefined',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || 'undefined',
      };
    },
    /**
     * @param {FormatterParams} params
     */
    scroll: ({ eventObj }) => {
      return {
        event: 'scroll',
        scroll_percentage: eventObj.data?.scrollPercentage || 'undefined',
        timestamp: new Date(eventObj?.timestamp)?.getTime() || 'undefined',
      };
    },
  });
})();
